/**
 * The pill row the owner Dokumente page walks down (COL-628), shared with the
 * "Dokument hinzufügen" dialog (COL-633) so filing a document and finding it
 * again look like the same path.
 */
import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/**
 * One filter pill. The top row is solid petrol when active; the rows below it
 * (`soft`) use the tint, so the level you are on reads as the darkest pill and
 * the path above it stays visible but quieter.
 */
const pillClass = (active: boolean, soft: boolean) =>
  cn(
    "inline-flex h-7 max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2FB3AA] focus-visible:ring-offset-1",
    active
      ? soft
        ? "border-[#0F4C4A] bg-[#E3EFED] text-[#0F4C4A]"
        : "border-[#0F4C4A] bg-[#0F4C4A] text-white"
      : "border-[#ECE7DD] bg-white text-[#47605E] hover:border-[#CFD9D7]"
  );

export function Pill({
  active,
  soft = false,
  onClick,
  children,
}: {
  active: boolean;
  soft?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={pillClass(active, soft)}
    >
      {children}
    </button>
  );
}

/** A pill that opens a list. Its label becomes the picked item, so the row of
 *  pills reads as the path you took. */
export function PillMenu({
  active,
  soft = false,
  label,
  heading,
  children,
}: {
  active: boolean;
  soft?: boolean;
  label: string;
  heading: string;
  children: ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={cn(pillClass(active, soft), "group")}>
        <span className="truncate">{label}</span>
        <ChevronDown
          className="h-3 w-3 flex-none transition-transform group-data-[state=open]:rotate-180"
          aria-hidden
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        collisionPadding={16}
        className="max-h-80 min-w-[240px] max-w-[min(340px,85vw)] overflow-y-auto rounded-xl"
      >
        <DropdownMenuLabel className="text-[10.5px] font-semibold uppercase tracking-wider text-[#7A8E8C]">
          {heading}
        </DropdownMenuLabel>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A row of pills one level down, fading in under the row above it. */
export function SubRow({
  indent = false,
  children,
}: {
  indent?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "ml-1.5 flex flex-wrap items-center gap-1.5 border-l-2 border-[#ECE7DD] pl-3.5 animate-in fade-in slide-in-from-top-1 duration-200 motion-reduce:animate-none",
        indent && "ml-6"
      )}
    >
      {children}
    </div>
  );
}
